import React from 'react';
import { useTranslation } from 'react-i18next';
import './CompatibilityAnimation.css';

// Wektorowa animacja współpracy falownik <-> magazyn. Jeden komponent, cztery stany
// przez prop `status` (waiting | compatible | unknown | not_compatible). Cała animacja
// żyje w CSS (data-status), więc zmiana stanu jest płynna, bez przeładowania grafiki.
//
// Kompozycja: po lewej falownik hybrydowy (obudowa + ekran z błyskawicą + dioda),
// po prawej magazyn energii (obudowa + 3 ogniwa ładujące się/rozładowujące + biegun),
// pomiędzy nimi kabel z przepływem energii. Obrys urządzeń dziedziczy z currentColor
// grupy (CSS steruje kolorem per stan).

const LABEL = {
  waiting: 'Wybierz falownik i magazyn, aby sprawdzić kompatybilność',
  compatible: 'Urządzenia są kompatybilne, energia przepływa',
  unknown: 'Kompatybilność wymaga weryfikacji',
  not_compatible: 'Urządzenia nie są kompatybilne',
};

// Status słowami pod kablem (odświeżenie 2026-10-14, wariant „para na żywo”): znacznik niesie kolor,
// tekst jest krótki. W stanie waiting środek zostaje pusty, podpisy urządzeń mówią, co wybrać.
const STATUS = {
  compatible: { tekst: 'zgodne', klucz: 'hero.zgodne', klasa: 'text-emerald-700 bg-emerald-50 dark:text-emerald-300 dark:bg-emerald-900/30' },
  unknown: { tekst: 'do sprawdzenia', klucz: 'hero.do_sprawdzenia', klasa: 'text-amber-700 bg-amber-50 dark:text-amber-300 dark:bg-amber-900/30' },
  not_compatible: { tekst: 'niezgodne', klucz: 'hero.niezgodne', klasa: 'text-rose-700 bg-rose-50 dark:text-rose-300 dark:bg-rose-900/30' },
};

const Podpis = ({ rola, nazwa, zacheta, wyrownanie }) => (
  <div className={`min-w-0 ${wyrownanie}`}>
    <div className="text-[11px] text-gray-400">{rola}</div>
    <div className={`text-sm leading-snug truncate ${nazwa ? 'font-semibold text-gray-900' : 'text-gray-400'}`} title={nazwa || undefined}>
      {nazwa || zacheta}
    </div>
  </div>
);

// Ścieżka kabla (między wtykami urządzeń) — delikatna fala. pathLength=100 normalizuje
// dash, więc impuls świetlny przesuwa się niezależnie od faktycznej długości.
const WIRE = 'M188 85 C 216 68, 264 102, 292 85';

// Props `falownik`, `magazyn` (nazwy wybranych modeli) i `szczegol` (np. „CAN, kod 00”) są
// opcjonalne: bez nich hero wygląda jak dawna animacja, z nimi pokazuje parę i wynik słowami.
const CompatibilityAnimation = ({ status = 'waiting', falownik = null, magazyn = null, szczegol = null }) => {
  const { t } = useTranslation('zgodnosc');
  const safe = LABEL[status] ? status : 'waiting';
  const znacznik = STATUS[safe];
  return (
    <div className="vv-compat-hero mx-auto w-full max-w-[440px]">
    <div className="vv-compat-anim" data-status={safe} role="img" aria-label={LABEL[safe]}>
      <svg viewBox="0 0 480 170" xmlns="http://www.w3.org/2000/svg">
        {/* Płasko, bez poświaty (odświeżenie 2026-10-14). */}

        {/* ---------- Kabel + impulsy energii ---------- */}
        <g className="link" fill="none" strokeLinecap="round">
          <path className="wire-base" d={WIRE} strokeWidth="3" />
          <path className="wire-pulse p1" d={WIRE} strokeWidth="3.5" pathLength="100" />
          <path className="wire-pulse p2" d={WIRE} strokeWidth="3.5" pathLength="100" />
        </g>

        {/* „Szukanie" połączenia w stanie waiting — pulsujące końcówki przy wtykach. */}
        <g fill="#94a3b8">
          <circle className="seek s1" cx="196" cy="85" r="4" />
          <circle className="seek s2" cx="284" cy="85" r="4" />
        </g>

        {/* ---------- Falownik hybrydowy (lewy) ---------- */}
        <g className="inverter device">
          <rect className="body" x="70" y="45" width="110" height="80" rx="17" fill="rgb(var(--vv-bg-white))" stroke="currentColor" strokeWidth="3" />
          {/* ekran (detal) */}
          <rect className="screen" x="92" y="57" width="66" height="50" rx="10" fill="currentColor" opacity="0.08" />
          {/* błyskawica */}
          <path className="bolt" d="M130 62 L113 88 L124 88 L119 106 L141 79 L129 79 Z" fill="currentColor" />
          {/* dioda statusu (detal) */}
          <circle className="led" cx="163" cy="57" r="3" />
          {/* wtyk po prawej */}
          <rect className="prong" x="178" y="80" width="12" height="10" rx="2.5" fill="currentColor" />
        </g>

        {/* ---------- Magazyn energii (prawy) ---------- */}
        <g className="battery device">
          <rect className="body" x="300" y="45" width="110" height="80" rx="17" fill="rgb(var(--vv-bg-white))" stroke="currentColor" strokeWidth="3" />
          {/* biegun na górze */}
          <rect x="345" y="37" width="20" height="10" rx="3" fill="currentColor" />
          {/* ogniwa — ładują się kolejno, potem rozładowują (animacja CSS) */}
          <rect className="cell cell1" x="316" y="63" width="20" height="44" rx="4" fill="currentColor" />
          <rect className="cell cell2" x="345" y="63" width="20" height="44" rx="4" fill="currentColor" />
          <rect className="cell cell3" x="374" y="63" width="20" height="44" rx="4" fill="currentColor" />
          {/* wtyk po lewej */}
          <rect className="prong" x="290" y="80" width="12" height="10" rx="2.5" fill="currentColor" />
        </g>

        {/* ---------- Znaczki stanu (środek) ----------
            Pozycja przez zewnętrzny <g transform> (atrybut SVG), a skala/opacity przez
            CSS na wewnętrznej .badge — inaczej CSS transform nadpisałby translate i
            znaczki lądowały w rogu (0,0). */}
        <g transform="translate(240 85)">
          {/* compatible: subtelny check */}
          <g className="badge badge-ok">
            <circle r="13" fill="rgb(var(--vv-bg-blue-50))" stroke="#2563eb" strokeWidth="2" />
            <path d="M-5 0 L-1.5 4 L5 -4" fill="none" stroke="#2563eb" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
          </g>
          {/* unknown: pytajnik / ostrzeżenie */}
          <g className="badge badge-warn">
            <circle r="13" fill="rgb(var(--vv-bg-amber-50))" stroke="#f59e0b" strokeWidth="2" />
            <path d="M-3.4 -4.2 A3.6 3.6 0 1 1 0.4 1.6 L0.4 3.4" fill="none" stroke="#f59e0b" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            <circle cx="0.4" cy="6.6" r="1.5" fill="#f59e0b" />
          </g>
          {/* not_compatible: przerwane połączenie */}
          <g className="badge badge-x">
            <circle r="13" fill="rgb(var(--vv-bg-rose-50))" stroke="#fb7185" strokeWidth="2" />
            <path d="M-4.5 -4.5 L4.5 4.5 M4.5 -4.5 L-4.5 4.5" fill="none" stroke="#fb7185" strokeWidth="2.6" strokeLinecap="round" />
          </g>
        </g>
      </svg>
    </div>
      {/* Podpisy pod urządzeniami i status słowami na środku. */}
      <div className="-mt-1 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-start gap-3 px-2">
        <Podpis rola={t('hero.falownik', 'Falownik')} nazwa={falownik} zacheta={t('hero.wybierz_model', 'wybierz model')} wyrownanie="text-right" />
        <div className="flex min-w-[88px] flex-col items-center gap-1 pt-1">
          {znacznik && (
            <span className={`rounded-md px-2.5 py-0.5 text-xs font-semibold ${znacznik.klasa}`}>{t(znacznik.klucz, znacznik.tekst)}</span>
          )}
          {znacznik && szczegol && <span className="text-[11px] text-gray-500 text-center">{szczegol}</span>}
        </div>
        <Podpis rola={t('hero.magazyn', 'Magazyn')} nazwa={magazyn} zacheta={t('hero.wybierz_model', 'wybierz model')} wyrownanie="text-left" />
      </div>
    </div>
  );
};

export default CompatibilityAnimation;
